import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { User as UserIcon, ShieldCheck, Building2, BadgeCheck, BarChart3, IdCard } from "lucide-react";

import "./TopBar.css";

/* ============ helpers ============ */
function normalizeUser(obj) {
  if (!obj) return null;
  const s = obj.data || obj.user || obj;
  return {
    ID: s.ID || s.id || s.username || s.name || "",
    Email: s.Email || s.email || "",
    Role: s.Role || s.role || "",
    Company: s.Company || s.company || "",
  };
}

const formatPLDate = (iso) =>
  new Date(iso).toLocaleDateString("pl-PL", { year: "numeric", month: "2-digit", day: "2-digit" });

function daysUntil(dateIso) {
  if (!dateIso || dateIso === "NO EXPIRE") return Infinity;
  const d = new Date(dateIso);
  const diff = d.getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function currentMonthRangeISO() {
  const now = new Date();
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0));
  const to   = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0));
  return { from: from.toISOString(), to: to.toISOString() };
}

function getLicenseLimit(typeRaw) {
  const t = String(typeRaw || "")
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

  if (t.includes("NOEXPIRE")) return 2000;
  if (t.includes("ENTERPRISE")) return 20;
  if (t.includes("PRO")) return 10;

  // IMPORTANT: DEMO before BASIC/STANDARD because "DEMO BASIC - 7 DNI" includes BASIC
  if (t.includes("DEMO")) return 99;

  if (t.includes("STANDARD") || t.includes("BASIC")) return 3;

  return 3;
}
// Backend count for the month (by company)
async function fetchMonthlyReportCount({ token, company, fromISO, toISO }) {
  const params = new URLSearchParams({ reportGeneratedBy: company, from: fromISO, to: toISO });
  const res = await fetch(`https://server229256.nazwa.pl/api/company-snapshots/count?${params}`, {
    headers: { Authorization: token },
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { msg += ` - ${JSON.stringify(await res.json())}`; } catch {}
    throw new Error(msg);
  }
  const data = await res.json();
  return data?.count ?? 0;
}

/* ============ component ============ */
export default function TopBar() {
  const location = useLocation();
  const [userPayload, setUserPayload] = useState(null);

  // Monthly reports badge
  const [monthlyCount, setMonthlyCount] = useState(null); // null => loading
  const [countErr, setCountErr] = useState("");

  const [nip, setNip] = useState(localStorage.getItem("nip") || "");

// tiny helper to normalize potential numeric NIP like 123456789.0
const normalizeNip = (v) => {
  if (!v && v !== 0) return "";
  const s = String(v);
  // drop anything after a dot (your tables sometimes show decimals)
  return s.includes(".") ? s.split(".")[0] : s;
};

useEffect(() => {
  const loadNipFromStorage = () => setNip(localStorage.getItem("nip") || "");

  // 1) initial from storage
  loadNipFromStorage();

  // 2) if missing, try to fetch first zakupy record using existing token
  const token = localStorage.getItem("token");
  const maybeFetch = async () => {
    if (nip) return; // already have it
    if (!token) return;

    try {
      // If your API supports limiting, this keeps it light. If not, the endpoint will just ignore it.
      const url = "https://server229256.nazwa.pl/api/zakupy?limit=1";
      const res = await fetch(url, { headers: { Authorization: token } });
      if (!res.ok) return;

      const arr = await res.json();
      const first = Array.isArray(arr) ? arr[0] : null;
      const got = normalizeNip(first?.NIP || "");
      if (got) {
        localStorage.setItem("nip", got);
        setNip(got);
        // notify other tabs/components that rely on this
        window.dispatchEvent(new Event("localStorageUpdate"));
      }
    } catch {
      /* silent — NIP is optional */
    }
  };

  maybeFetch();

  // react to storage changes (cross-tab) and our custom event (same tab)
  const onStorage = (e) => {
    if (!e.key || e.key === "nip") loadNipFromStorage();
  };
  const onCustom = () => loadNipFromStorage();

  window.addEventListener("storage", onStorage);
  window.addEventListener("localStorageUpdate", onCustom);
  return () => {
    window.removeEventListener("storage", onStorage);
    window.removeEventListener("localStorageUpdate", onCustom);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [/* no deps: run once, inner logic guards repeats */]);


  // Load user from localStorage / JWT; update when storage changes
  useEffect(() => {
    const load = () => {
      try {
        const candidates = ["user_payload", "user", "authUser", "profile"];
        for (const key of candidates) {
          const raw = localStorage.getItem(key);
          if (raw) {
            const parsed = JSON.parse(raw);
            const norm = normalizeUser(parsed);
            if (norm?.ID || norm?.Email) {
              setUserPayload(norm);
              return;
            }
          }
        }
        const token = localStorage.getItem("token");
        if (token) {
          const d = jwtDecode(token);
          const norm = normalizeUser({
            ID: d.ID || d.UserID || d.sub || d.name,
            Email: d.Email || d.email,
            Role: d.Role || d.role,
            Company: d.Company || d.company,
          });
          setUserPayload(norm);
        }
      } catch {
        setUserPayload(null);
      }
    };
    load();

    const onStorage = (e) => {
      if (!e.key || ["user_payload", "user", "authUser", "profile", "token"].includes(e.key)) load();
    };
    const onCustom = () => load();

    window.addEventListener("storage", onStorage);
    window.addEventListener("localStorageUpdate", onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("localStorageUpdate", onCustom);
    };
  }, []);


const { licenseType, licenseExpire, nearExpire, isNoExpire, licenseLimit } = useMemo(() => {
  const savedToken = localStorage.getItem("token");
  if (!savedToken) {
    return {
      licenseType: "Brak",
      licenseExpire: "-",
      nearExpire: false,
      isNoExpire: false,
      licenseLimit: getLicenseLimit("BASIC"),
    };
  }
  try {
    const decoded = jwtDecode(savedToken);
    const typeRaw = decoded.LicenseType || "Brak";
    const expRaw  = decoded.LicenseExpire ?? "-";

    // detect no-expire by type, explicit flag, or far-future date
    const typeHasNoExp = String(typeRaw).toUpperCase().includes("NO_EXPIRE") ||
                         String(typeRaw).toUpperCase().includes("NO EXPIRE");
    const expIsFlag    = String(expRaw).toUpperCase() === "NO EXPIRE";

    let yearIsFar = false;
    if (!expIsFlag && typeof expRaw === "string") {
      const dt = new Date(expRaw);
      yearIsFar = !isNaN(dt) && dt.getUTCFullYear() >= 2099;
    }

    const isNoExp = typeHasNoExp || expIsFlag || yearIsFar;

    return {
      licenseType: typeRaw,
      licenseExpire: isNoExp ? "Bezterminowa" : formatPLDate(expRaw),
      nearExpire: !isNoExp && daysUntil(expRaw) <= 14,
      isNoExpire: isNoExp,
      licenseLimit: getLicenseLimit(typeRaw),
    };
  } catch {
    return {
      licenseType: "Brak",
      licenseExpire: "-",
      nearExpire: false,
      isNoExpire: false,
      licenseLimit: getLicenseLimit("BASIC"),
    };
  }
}, []);


  // Fetch monthly count (by company) when user/company ready
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !userPayload?.Company) {
      setMonthlyCount(null);
      return;
    }
    const { from, to } = currentMonthRangeISO();

    setMonthlyCount(null);
    setCountErr("");

    fetchMonthlyReportCount({
      token,
      company: userPayload.Company,
      fromISO: from,
      toISO: to,
    })
      .then((cnt) => setMonthlyCount(cnt))
      .catch((e) => {
        console.warn("⚠️ Monthly report count fetch failed:", e?.message || e);
        setCountErr("—");
        setMonthlyCount(0);
      });
  }, [userPayload?.Company]);

  // Only show on protected pages
  const protectedRoots = [
    "/dashboard",
    "/sprzedaz",
    "/zakupy",
    "/daily-sales",
    "/klienci",
    "/import",
    "/admin",
    "/raportonas",
    "/adminpanel",
    "/raporty",
    "/kontrahent",
  ];
  const show = protectedRoots.some((p) => location.pathname.startsWith(p));
  if (!show) return null;

  const usedText = monthlyCount === null ? "…" : monthlyCount;
  const limitText = licenseLimit ?? "—";




  return (
    <div className="topbar" role="region" aria-label="Pasek statusu użytkownika">
      <div className="topbar-row">
        <div className="tb-chip user" aria-label="Użytkownik" title="Użytkownik">
          <span className="icon" aria-hidden="true"><UserIcon /></span>
          <span></span>
          <strong>{userPayload?.ID || "—"}</strong>
        </div>

        <div className="tb-chip role" aria-label="Rola" title="Rola">
          <span className="icon" aria-hidden="true"><ShieldCheck /></span>
          <span></span>
          <strong>{userPayload?.Role || "—"}</strong>
        </div>

        <div className="tb-chip company" aria-label="Firma" title="Firma">
          <span className="icon" aria-hidden="true"><Building2 /></span>
          <span></span>
          <strong>{userPayload?.Company || "—"}</strong>
        </div>
        <div className="tb-chip nip" aria-label="NIP" title={`NIP: ${nip || "—"}`}>
  <span className="icon" aria-hidden="true"><IdCard /></span>
  <span></span>
  <strong>{nip || "—"}</strong>
</div>


        <div
          className={`tb-chip license${nearExpire ? " near-expire" : ""}`}
          aria-label="Licencja"
          title={`Licencja: ${licenseType}${isNoExpire ? " (bezterminowa)" : ""}`}
        >
          <span className="icon" aria-hidden="true"><BadgeCheck /></span>
          <span></span>
          <strong>{licenseType}</strong>
          <em className="license-exp">
  {isNoExpire ? "Bezterminowa" : licenseExpire}
</em>
        </div>

        {/* Monthly reports: used / limit */}
        <div
          className="tb-chip reports"
          aria-label="Raporty w tym miesiącu"
          title={`Zużycie w tym miesiącu: ${usedText}/${limitText}`}
        >
          <span className="icon" aria-hidden="true"><BarChart3 /></span>
          <span>Raporty (miesiąc)</span>
          <strong>{usedText}/{limitText}</strong>
        </div>
      </div>
    </div>
  );
}
